import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PayPalPlan, SubscriptionLimit, TIER_LABELS, TIER_DESCRIPTIONS } from '../types';
import { SubscriptionStatus } from '../../../services/paymentService';
import { calculateAllDiscounts, formatDiscountPercentage } from '../../../utils/discountCalculator';
import PayPalButton from '../PayPalButton';
import { usePermissions, type Feature } from '../../../contexts/PermissionContext';
import { getTierFeatures } from '../../../utils/tierFeatures';
import type { PermissionDisplayMetadata } from '../../../services/permissionService';

interface PlanCardProps {
  plan: PayPalPlan;
  limits: SubscriptionLimit | undefined;
  subscriptionStatus: SubscriptionStatus | null;
  activeTier: string;
  activeBillingCycle: string | null;
  hasPaidSubscription: boolean;
  hasRoleBasedAccess: boolean;
  hasPendingDowngrade: boolean;
  paypalSdkLoaded: boolean;
  upgradeLoading: string | null;
  downgradeLoading: string | null;
  containerId: string;
  monthlyPlan?: PayPalPlan; // For annual plans, to calculate discounts
  freeLimits?: SubscriptionLimit | null;
  starterLimits?: SubscriptionLimit | null;
  isFamilyMemberNotOwner?: boolean; // True if user is a family member (not owner)
  onUpgrade: (planId: string) => void;
  onDowngrade: (planId: string) => void;
  onSubscriptionSuccess: () => void;
}

export default function PlanCard({
  plan,
  limits,
  subscriptionStatus,
  activeTier,
  activeBillingCycle,
  hasPaidSubscription,
  hasRoleBasedAccess,
  hasPendingDowngrade,
  paypalSdkLoaded,
  upgradeLoading,
  downgradeLoading,
  containerId,
  monthlyPlan,
  freeLimits,
  starterLimits,
  isFamilyMemberNotOwner = false,
  onUpgrade,
  onDowngrade,
  onSubscriptionSuccess,
}: PlanCardProps) {
  const { getPermissionMetadata } = usePermissions();
  
  // Get standardized features using shared utility
  const features = getTierFeatures({
    tier: plan.tier,
    limits: limits || null,
    getPermissionMetadata: getPermissionMetadata as (feature: Feature) => PermissionDisplayMetadata | undefined,
    freeLimits,
    starterLimits,
  });
  
  // Check if this is the exact same plan (same tier + same billing cycle)
  const isExactCurrentPlan = activeTier === plan.tier && activeBillingCycle === plan.billing_cycle;
  // Check if this is the same tier but different billing cycle (allow switching)
  const isSameTierDifferentCycle = activeTier === plan.tier && activeBillingCycle !== plan.billing_cycle && activeBillingCycle !== null;
  // Determine if this is upgrade, downgrade, or new subscription
  const tierPriority: Record<string, number> = { free: 1, starter: 2, pro: 3, family: 4 };
  const currentPriority = tierPriority[activeTier] || 1;
  const planPriority = tierPriority[plan.tier] || 1;
  const isDowngrade = currentPriority > planPriority && hasPaidSubscription;
  const isUpgrade = currentPriority < planPriority && hasPaidSubscription;
  const isNewSubscription = !hasPaidSubscription || activeTier === 'free';
  const isActive = isExactCurrentPlan;
  
  
  // Check if user has pending downgrade to this tier + billing cycle
  const hasPendingDowngradeToThisPlan = subscriptionStatus?.downgrade_pending &&
                                        subscriptionStatus?.pending_tier === plan.tier &&
                                        subscriptionStatus?.pending_billing_cycle === plan.billing_cycle;
  
  // Check if free upgrade is available for this Pro plan
  const isFreeUpgradeAvailable = plan.tier === 'pro' && 
                                 plan.billing_cycle === activeBillingCycle && (
    (plan.billing_cycle === 'monthly' && subscriptionStatus?.free_upgrade_available_monthly === true) ||
    (plan.billing_cycle === 'annual' && subscriptionStatus?.free_upgrade_available_annual === true)
  );
  
  // Calculate discounts using frontend utility
  const discounts = calculateAllDiscounts(plan, monthlyPlan);
  
  return (
    <Card className={`relative flex flex-col h-full ${
      isActive ? 'ring-2 ring-purple-500' : ''
    } ${
      hasPendingDowngradeToThisPlan ? 'ring-2 ring-yellow-500 border-yellow-500' : ''
    } ${
      isFreeUpgradeAvailable ? 'ring-2 ring-green-500 border-green-500' : ''
    } subscription-tier-card w-full`}>
      {isActive && (
        <Badge className="absolute top-2 right-2" variant="default">
          Current
        </Badge>
      )}
      {hasPendingDowngradeToThisPlan && (
        <Badge className="absolute top-2 right-2 bg-yellow-500 text-yellow-950" variant="default">
          {subscriptionStatus?.next_billing_date 
            ? `Starting ${new Date(subscriptionStatus.next_billing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            : 'Starting Next Month'}
        </Badge>
      )}
      {isSameTierDifferentCycle && !isActive && !hasPendingDowngradeToThisPlan && (
        <Badge className="absolute top-2 right-2" variant="outline">
          Switch to {plan.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}
        </Badge>
      )}
      {isDowngrade && !isActive && !hasPendingDowngradeToThisPlan && (
        <Badge className="absolute top-2 right-2" variant="secondary">
          Downgrade
        </Badge>
      )}
      {isFreeUpgradeAvailable && !isActive && !hasPendingDowngradeToThisPlan && (
        <Badge className="absolute top-2 right-2 bg-green-500 text-white" variant="default">
          Free Upgrade Available
        </Badge>
      )}
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-xl capitalize">{TIER_LABELS[plan.tier] || plan.tier}</CardTitle>
        </div>
        <CardDescription>
          {TIER_DESCRIPTIONS[plan.tier] || `${plan.tier} plan`}
          {hasPendingDowngradeToThisPlan && subscriptionStatus?.next_billing_date && (
            <span className="block mt-1 text-yellow-600 dark:text-yellow-400">
              Changes take effect on {new Date(subscriptionStatus.next_billing_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          )}
          {isFreeUpgradeAvailable && (
            <span className="block mt-1 text-green-600 dark:text-green-400 font-medium">
              No proration will be applied - upgrade is free!
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col flex-grow overflow-visible">
        <div className="mb-4">
          {plan.base_price !== null && plan.base_price !== undefined && plan.discounted_price !== null && plan.discounted_price !== undefined && plan.base_price !== plan.discounted_price ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-lg line-through text-muted-foreground">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: plan.currency_code || 'USD',
                  }).format(plan.base_price)}
                </span>
                <span className="text-3xl font-bold">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: plan.currency_code || 'USD',
                  }).format(plan.discounted_price)}
                </span>
                {discounts.promotionDiscount !== null && (
                  <Badge variant="default" className="bg-green-500">
                    {formatDiscountPercentage(discounts.promotionDiscount)} OFF
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-muted-foreground text-sm">
                  {plan.billing_cycle === 'monthly' ? 'per month' : 'per year'}
                </span>
                {plan.billing_cycle === 'annual' && discounts.annualSavings !== null && (
                  <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-500 border-blue-500/20">
                    Save {formatDiscountPercentage(discounts.annualSavings)} vs monthly
                  </Badge>
                )}
              </div>
            </div>
          ) : plan.billing_cycle === 'annual' && plan.twelve_x_monthly_price !== null && plan.price !== null && plan.twelve_x_monthly_price > plan.price ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-lg line-through text-muted-foreground">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: plan.currency_code || 'USD',
                  }).format(plan.twelve_x_monthly_price)}
                </span>
                <span className="text-3xl font-bold">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: plan.currency_code || 'USD',
                  }).format(plan.price)}
                </span>
                {discounts.annualSavings !== null && (
                  <Badge variant="default" className="bg-green-500">
                    {formatDiscountPercentage(discounts.annualSavings)} OFF
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-muted-foreground text-sm">
                  per year
                </span>
                {discounts.annualSavings !== null && (
                  <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-500 border-blue-500/20">
                    Save {formatDiscountPercentage(discounts.annualSavings)} vs monthly
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <div>
              <span className="text-3xl font-bold">
                {plan.price !== null && plan.price !== undefined 
                  ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: plan.currency_code || 'USD',
                    }).format(plan.price)
                  : 'Price N/A'}
              </span>
              <span className="text-muted-foreground">
                {plan.billing_cycle === 'monthly' ? 'per month' : 'per year'}
              </span>
            </div>
          )}
        </div>
        <ul className="space-y-2 mb-4 text-sm flex-grow">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2">
              {!feature.isHeader && (
                feature.included ? (
                  <span className="text-green-500">✓</span>
                ) : (
                  <span className="text-red-500">✗</span>
                )
              )}
              {feature.isHeader && <span className="flex-shrink-0" />}
              <span className={feature.bold ? 'font-medium' : feature.included ? '' : 'text-muted-foreground'}>
                {feature.text}
              </span>
            </li>
          ))}
        </ul>
        {/* Show appropriate button based on subscription state */}
        {isFamilyMemberNotOwner ? (
          // User is a family member - cannot purchase own subscription
          <div className="mt-auto w-full">
            <Button className="w-full" variant="outline" disabled>
              Managed by Family Owner
            </Button>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              You must leave your family plan before purchasing your own subscription
            </p>
          </div>
        ) : hasRoleBasedAccess ? (
          // User has role-based access - show message to contact admin
          <div className="mt-auto w-full">
            <Button className="w-full" variant="outline" disabled>
              {activeTier === plan.tier ? 'Current Access (Role-Based)' : 'Managed by Admin'}
            </Button>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Contact admin team to update your subscription
            </p>
          </div>
        ) : isExactCurrentPlan ? (
          <div className="mt-auto w-full">
            <Button className="w-full" variant="default" disabled>
              Current Plan
            </Button>
          </div>
        ) : isSameTierDifferentCycle ? (
          <div className="mt-auto w-full">
            <Button
              className="w-full"
              variant="default"
              onClick={() => onUpgrade(plan.paypal_plan_id)}
              disabled={upgradeLoading === plan.paypal_plan_id}
            >
              {upgradeLoading === plan.paypal_plan_id ? (
                <>
                                    Processing...
                </>
              ) : (
                `Switch to ${plan.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}`
              )}
            </Button>
          </div>
        ) : isDowngrade ? (
          <div className="mt-auto w-full">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => onDowngrade(plan.paypal_plan_id)}
              disabled={downgradeLoading === plan.paypal_plan_id || hasPendingDowngradeToThisPlan}
            >
              {downgradeLoading === plan.paypal_plan_id ? (
                <>
                                    Processing...
                </>
              ) : hasPendingDowngradeToThisPlan ? (
                'Downgrade Scheduled'
              ) : (
                `Downgrade to ${TIER_LABELS[plan.tier] || plan.tier}`
              )}
            </Button>
          </div>
        ) : isUpgrade ? (
          <div className="mt-auto w-full">
            <Button
              className="w-full"
              variant="default"
              onClick={() => onUpgrade(plan.paypal_plan_id)}
              disabled={upgradeLoading === plan.paypal_plan_id}
            >
              {upgradeLoading === plan.paypal_plan_id ? (
                <>
                                    Processing...
                </>
              ) : (
                `Upgrade to ${TIER_LABELS[plan.tier] || plan.tier}`
              )}
            </Button>
            {hasPendingDowngrade && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2 text-center">
                Upgrading will cancel your pending cancellation
              </p>
            )}
          </div>
        ) : isNewSubscription && paypalSdkLoaded ? (
          hasPendingDowngrade ? (
            <div className="mt-auto w-full">
              <Button
                className="w-full"
                variant="default"
                disabled
                title="Subscription change pending"
              >
                Subscription Change Pending
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Cannot subscribe while a downgrade is pending. Please wait for the pending change to complete.
              </p>
            </div>
          ) : (
            <div className="mt-auto w-full paypal-buttons-wrapper" style={{ colorScheme: 'none' }}>
              <PayPalButton
                planId={plan.paypal_plan_id}
                containerId={containerId}
                tier={plan.tier}
                billingCycle={plan.billing_cycle}
                onSubscriptionSuccess={onSubscriptionSuccess}
              />
            </div>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

